import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { GrantAffinityProfile } from "@/api/grant_affinity_profile";
import { explainGrantFit, listGrantRecommendations } from "@/api/grant_recommendations";
import type { DiscoverGrant } from "@/components/grant_suite/grantDiscoverUi";

type UseGrantDiscoverRecommendationsOptions = {
  municipalityId?: number;
  canUseGrantDiscover: boolean;
  hasSearched: boolean;
  catalogScope: string;
  stateFilter: string;
  excludeFormula: boolean;
  affinityProfile: GrantAffinityProfile | null;
  profileComplete: boolean;
  profileAwaitingEmbed: boolean;
  profileLoading: boolean;
  profileWizardOpen: boolean;
  setProfileWizardOpen: (open: boolean) => void;
  onFundingPrioritiesPrefill?: (priorities: string) => void;
  onDefaultSortFit?: () => void;
};

function appendUniqueGrants(existing: DiscoverGrant[], incoming: DiscoverGrant[]) {
  const seen = new Set(existing.map((grant) => grant.id));
  const appended = incoming.filter((grant) => !seen.has(grant.id));
  return [...existing, ...appended];
}

export function useGrantDiscoverRecommendations({
  municipalityId,
  canUseGrantDiscover,
  hasSearched,
  catalogScope,
  stateFilter,
  excludeFormula,
  affinityProfile,
  profileComplete,
  profileAwaitingEmbed,
  profileLoading,
  profileWizardOpen,
  setProfileWizardOpen,
  onFundingPrioritiesPrefill,
  onDefaultSortFit,
}: UseGrantDiscoverRecommendationsOptions) {
  const queryClient = useQueryClient();
  const queryPrefilledRef = useRef(false);
  const defaultSortAppliedRef = useRef(false);
  const [recommendedPage, setRecommendedPage] = useState(1);
  const [recommendedGrants, setRecommendedGrants] = useState<DiscoverGrant[]>([]);
  const [recommendedTotal, setRecommendedTotal] = useState(0);
  const [recommendedInsights, setRecommendedInsights] = useState<string | null>(null);
  const [fitExplanations, setFitExplanations] = useState<Record<number, string>>({});
  const [explainingFitGrants, setExplainingFitGrants] = useState<Record<number, boolean>>({});
  const [fitExplanationErrors, setFitExplanationErrors] = useState<Record<number, string>>({});

  const {
    data: recommendationsData,
    isLoading: recommendationsLoading,
    isFetching: recommendationsFetching,
    error: recommendationsQueryError,
  } = useQuery({
    queryKey: ["grant_recommendations", municipalityId, recommendedPage, catalogScope, stateFilter, excludeFormula],
    queryFn: () =>
      listGrantRecommendations(municipalityId!, {
        page: recommendedPage,
        catalogScope: catalogScope || undefined,
        state: stateFilter || undefined,
        excludeFormula: excludeFormula || undefined,
      }),
    enabled: canUseGrantDiscover && Boolean(municipalityId) && profileComplete && !hasSearched,
  });

  useEffect(() => {
    if (!recommendationsData) return;
    setRecommendedGrants((prev) =>
      recommendedPage === 1
        ? recommendationsData.grants
        : appendUniqueGrants(prev, recommendationsData.grants),
    );
    setRecommendedTotal(recommendationsData.total_count);
    if (recommendationsData.insights) {
      setRecommendedInsights(recommendationsData.insights);
    }
  }, [recommendationsData, recommendedPage]);

  useEffect(() => {
    queryPrefilledRef.current = false;
    defaultSortAppliedRef.current = false;
    setRecommendedPage(1);
    setRecommendedGrants([]);
    setRecommendedTotal(0);
    setRecommendedInsights(null);
  }, [municipalityId]);

  useEffect(() => {
    if (!profileComplete || defaultSortAppliedRef.current) return;
    onDefaultSortFit?.();
    defaultSortAppliedRef.current = true;
  }, [profileComplete, onDefaultSortFit]);

  useEffect(() => {
    if (!canUseGrantDiscover || queryPrefilledRef.current || !affinityProfile?.funding_priorities) return;
    onFundingPrioritiesPrefill?.(affinityProfile.funding_priorities);
    queryPrefilledRef.current = true;
  }, [canUseGrantDiscover, affinityProfile?.funding_priorities, onFundingPrioritiesPrefill]);

  const resetRecommendationPaging = () => {
    setRecommendedPage(1);
    setRecommendedGrants([]);
    setRecommendedTotal(0);
  };

  const handleProfileSaved = () => {
    if (!municipalityId) return;
    queryPrefilledRef.current = false;
    resetRecommendationPaging();
    queryClient.invalidateQueries({ queryKey: ["grant_affinity_profile", municipalityId] });
    queryClient.removeQueries({ queryKey: ["grant_recommendations", municipalityId] });
  };

  const handleExplainFit = async (grant: DiscoverGrant) => {
    if (!canUseGrantDiscover || !municipalityId) return;

    setFitExplanationErrors((prev) => {
      const next = { ...prev };
      delete next[grant.id];
      return next;
    });
    setExplainingFitGrants((prev) => ({ ...prev, [grant.id]: true }));

    try {
      const explanation = await explainGrantFit(municipalityId, grant.id);
      setFitExplanations((prev) => ({ ...prev, [grant.id]: explanation }));
    } catch (err) {
      setFitExplanationErrors((prev) => ({
        ...prev,
        [grant.id]: err instanceof Error ? err.message : "Could not explain fit",
      }));
    } finally {
      setExplainingFitGrants((prev) => ({ ...prev, [grant.id]: false }));
    }
  };

  const visibleRecommendedGrants =
    recommendedPage === 1 && recommendationsData ? recommendationsData.grants : recommendedGrants;
  const visibleRecommendedTotal =
    recommendedPage === 1 && recommendationsData ? recommendationsData.total_count : recommendedTotal;

  const hasMoreRecommendations = visibleRecommendedGrants.length < visibleRecommendedTotal;
  const recommendationsLoadingMore = recommendationsFetching && recommendedPage > 1;
  const recommendationsInitialLoading =
    (recommendationsLoading || profileLoading) && recommendedPage === 1;

  return {
    affinityProfile,
    profileComplete,
    profileAwaitingEmbed,
    profileLoading,
    profileWizardOpen,
    setProfileWizardOpen,
    handleProfileSaved,
    resetRecommendationPaging,
    recommendedGrants: visibleRecommendedGrants,
    recommendedTotal: visibleRecommendedTotal,
    recommendedInsights,
    setRecommendedInsights,
    recommendedPage,
    setRecommendedPage,
    hasMoreRecommendations,
    recommendationsLoadingMore,
    recommendationsInitialLoading,
    recommendationsQueryError,
    fitExplanations,
    explainingFitGrants,
    fitExplanationErrors,
    handleExplainFit,
  };
}
